import { proximaApi } from '../proximaApi'
import { type DisbursementAccount, type DisbursementInstructionSummary } from '../schema-dtos/AssetHolderDisbursementDomainDtos'
import {
  type CharityDisbursementDetails,
  type CharityOrganisationDetails, type CreateCharityDisbursementPayload,
  type CreateDisbursementInstructionPayload,
  type VerifyDisbursementAccountPayload
} from '../schema-dtos/AssetHolderDisbursementPayloadDtos'

const SUB_URL = '/payments/disbursements'

export const disbursementApi = proximaApi.injectEndpoints({
  endpoints: (builder) => ({
    getDisbursementAccounts: builder.query<DisbursementAccount[], Partial<number>>({
      query: (assetHolderId: number) => ({
        url: `${SUB_URL}/asset-holders/${assetHolderId}/accounts`,
        method: 'GET'
      }),
      providesTags: ['AssetHolderFinancials']
    }),

    createDisbursementAccount: builder.mutation({
      query: (payload: any) => ({
        url: `${SUB_URL}/asset-holders/${payload.assetHolderId}/accounts/create`,
        method: 'POST',
        body: payload.body
      }),
      invalidatesTags: ['AssetHolderFinancials', 'AssetHolderSettlement']
    }),

    verifyDisbursementAccount: builder.mutation({
      query: (payload: VerifyDisbursementAccountPayload) => ({
        url: `${SUB_URL}/asset-holders/${payload.assetHolderId}/accounts/verify`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['AssetHolderFinancials', 'AssetHolderSettlement']
    }),

    cancelVerifyDisbursementAccount: builder.mutation({
      query: (payload: VerifyDisbursementAccountPayload) => ({
        url: `${SUB_URL}/asset-holders/${payload.assetHolderId}/accounts/verify-cancel`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['AssetHolderFinancials', 'AssetHolderSettlement']
    }),

    getDisbursementAccountForVerification: builder.query<DisbursementAccount, Partial<VerifyDisbursementAccountPayload>>({
      query: (payload: VerifyDisbursementAccountPayload) => ({
        url: `${SUB_URL}/asset-holders/${payload.assetHolderId}/accounts/verification-account/${payload.token}`,
        method: 'GET'
      }),
      providesTags: ['AssetHolderFinancials']
    }),

    getPendingDisbursementInstructions: builder.query<DisbursementInstructionSummary[], Partial<number>>({
      query: (assetHolderId: number) => ({
        url: `${SUB_URL}/asset-holders/${assetHolderId}/instructions`,
        method: 'GET'
      }),
      providesTags: ['AssetHolderFinancials']
    }),

    cancelDisbursementInstruction: builder.mutation({
      query: (payload: any) => ({
        url: `${SUB_URL}/asset-holders/${payload.assetHolderId}/instructions/cancel`,
        method: 'POST',
        body: payload.body
      }),
      invalidatesTags: ['AssetHolderFinancials', 'AssetHolderSettlement']
    }),

    getDisbursementFees: builder.query<undefined, Partial<void>>({
      query: () => ({
        url: `${SUB_URL}/fees`,
        method: 'GET'
      })
    }),

    createDisbursementInstruction: builder.mutation<undefined, Partial<{ assetHolderId: number, body: CreateDisbursementInstructionPayload }>>({
      query: (payload) => ({
        url: `${SUB_URL}/asset-holders/${payload.assetHolderId}/instructions/create`,
        method: 'POST',
        body: payload.body
      }),
      invalidatesTags: ['AssetHolderFinancials', 'AssetHolderSettlement']
    }),

    getCharityOptions: builder.query<CharityOrganisationDetails[], Partial<number>>({
      query: (assetHolderId: number) => ({
        url: `${SUB_URL}/asset-holders/${assetHolderId}/charities`,
        method: 'GET'
      }),
      providesTags: ['AssetHolderFinancials']
    }),

    createCharityDisbursementOption: builder.mutation<undefined, Partial<{ assetHolderId: number, body: CreateCharityDisbursementPayload }>>({
      query: (payload) => ({
        url: `${SUB_URL}/asset-holders/${payload.assetHolderId}/charities/create`,
        method: 'POST',
        body: payload.body
      }),
      invalidatesTags: ['AssetHolderFinancials', 'AssetHolderSettlement']
    }),

    getCharityDisbursements: builder.query<CharityDisbursementDetails[], Partial<number>>({
      query: (assetHolderId: number) => ({
        url: `${SUB_URL}/asset-holders/${assetHolderId}/charities/instructions`,
        method: 'GET'
      }),
      providesTags: ['AssetHolderFinancials']
    })

  }),

  overrideExisting: false
})

export const useGetDisbursementAccountsQuery = disbursementApi.endpoints.getDisbursementAccounts.useQuery
export const useCreateDisbursementAccountMutation = disbursementApi.endpoints.createDisbursementAccount.useMutation
export const useVerifyDisbursementAccountMutation = disbursementApi.endpoints.verifyDisbursementAccount.useMutation
export const useCancelVerifyDisbursementAccountMutation = disbursementApi.endpoints.cancelVerifyDisbursementAccount.useMutation
export const useGetAccountForVerificationQuery = disbursementApi.endpoints.getDisbursementAccountForVerification.useQuery
export const useGetPendingDisbursementInstructionsQuery = disbursementApi.endpoints.getPendingDisbursementInstructions.useQuery
export const useCancelDisbursementInstructionMutation = disbursementApi.endpoints.cancelDisbursementInstruction.useMutation
export const useGetDisbursementFeesQuery = disbursementApi.endpoints.getDisbursementFees.useQuery
export const useCreateDisbursementInstructionMutation = disbursementApi.endpoints.createDisbursementInstruction.useMutation

export const useGetCharityOptionsQuery = disbursementApi.endpoints.getCharityOptions.useQuery
export const useCreateCharityDisbursementOptionsQuery = disbursementApi.endpoints.createCharityDisbursementOption.useMutation
export const useGetCharityDisbursementsQuery = disbursementApi.endpoints.getCharityDisbursements.useQuery
